import { gatewayBaseUrl } from './env';

/**
 * Hook-shaped fetcher used by the REST endpoints (`/api/sessions`,
 * `/api/files/*`). Built around a Clerk `getToken()` thunk so the same
 * client can run inside React (Clerk's `useAuth().getToken`) AND inside
 * tests where a static token is fine.
 *
 * The Worker's auth shim (`apps/cloudflare/src/auth.ts`) accepts both
 * `Authorization: Bearer …` and `?token=…` for browsers. We always
 * send the Bearer header here because regular fetches CAN set custom
 * headers (it's the WebSocket upgrade that can't).
 */
export interface ApiClient {
  /** Lists every session for the signed-in user. */
  listSessions: () => Promise<SessionMeta[]>;
  /** Deletes a session and its sandbox. */
  deleteSession: (sessionId: string) => Promise<void>;
  /**
   * Forks a session at a specific message — DD-BLB-24. Returns the new
   * session's metadata (caller appends it to the local tab list).
   */
  forkSession: (
    sourceSessionId: string,
    atMessageId: string,
    name?: string,
  ) => Promise<SessionMeta>;
  /** Lists direct children at `path` under the chosen scope. */
  listFiles: (
    scope: 'persistent' | 'workspace',
    path: string,
    opts?: { sessionId?: string; cursor?: string; signal?: AbortSignal },
  ) => Promise<FileListing>;
  /** Reads a file's text content (UTF-8 decoded). */
  readFileText: (
    scope: 'persistent' | 'workspace',
    path: string,
    opts?: { sessionId?: string; signal?: AbortSignal },
  ) => Promise<{ text: string; mediaType: string }>;
  /** Mint a fresh 8-char Mac pairing code. 5-min TTL, single-use. */
  issueMacPairingCode: () => Promise<{ code: string; expiresAt: number }>;
  /** List paired Mac devices for the signed-in user. Polls in the modal. */
  listMacDevices: (opts?: { signal?: AbortSignal }) => Promise<MacDevice[]>;
  /** Revoke a paired Mac device by id. Idempotent. */
  revokeMacDevice: (deviceId: string) => Promise<void>;
}

/**
 * Shape returned by `GET /api/mac/devices`. Mirrors the fields surfaced
 * by `MacControlPlane.listDevices` plus the live `online` flag derived
 * from the MacEnv DO at request time.
 */
export interface MacDevice {
  deviceId: string;
  displayName: string;
  os: string;
  osVersion: string;
  /** Unix ms when the user paired this device. */
  pairedAt: number;
  /** Unix ms updated on every refresh round-trip; null until first refresh. */
  lastSeenAt: number | null;
  /** True when the MacEnv DO has an open WS for this deviceId. */
  online: boolean;
  /**
   * Slugified device label per DD-MAC-36 (e.g. `mingkais-macbook-pro`).
   * Used as the `<deviceLabel>--<skillName>` namespace prefix in
   * Bluebird's skill registry. Falls back to `device-<first8>` for
   * legacy rows backfilled by migration 0003.
   */
  label: string;
}

export interface SessionMeta {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  messageCount: number;
}

export interface FileEntry {
  name: string;
  kind: 'file' | 'dir';
  size?: number;
  mediaType?: string;
}

export interface FileListing {
  entries: FileEntry[];
  cursor?: string;
}

interface CreateApiClientOptions {
  getToken: () => Promise<string | null>;
}

/** Subset of REST errors the front-end recognises and shapes for UI. */
export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

export function createApiClient({
  getToken,
}: CreateApiClientOptions): ApiClient {
  async function authedFetch(
    pathAndQuery: string,
    init?: RequestInit,
  ): Promise<Response> {
    const url = `${gatewayBaseUrl()}${pathAndQuery}`;
    const token = await getToken();
    const headers = new Headers(init?.headers);
    if (token) headers.set('authorization', `Bearer ${token}`);
    const res = await fetch(url, {
      ...init,
      headers,
      credentials: 'include',
    });
    return res;
  }

  async function jsonOrThrow<T>(res: Response, action: string): Promise<T> {
    if (res.ok) {
      return (await res.json()) as T;
    }
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      // Non-JSON error response.
    }
    const msg =
      (body && typeof body === 'object' && 'message' in body
        ? String((body as { message: unknown }).message)
        : null) ?? `${action} failed (${res.status})`;
    throw new ApiError(res.status, msg, body);
  }

  return {
    async listSessions(): Promise<SessionMeta[]> {
      const res = await authedFetch('/api/sessions');
      const body = await jsonOrThrow<{ sessions: SessionMeta[] }>(
        res,
        'list sessions',
      );
      return body.sessions;
    },

    async deleteSession(sessionId: string): Promise<void> {
      const res = await authedFetch(
        `/api/sessions/${encodeURIComponent(sessionId)}`,
        { method: 'DELETE' },
      );
      if (res.status === 204 || res.ok) return;
      await jsonOrThrow(res, 'delete session');
    },

    async forkSession(
      sourceSessionId: string,
      atMessageId: string,
      name?: string,
    ): Promise<SessionMeta> {
      const res = await authedFetch(
        `/api/sessions/${encodeURIComponent(sourceSessionId)}/fork`,
        {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ atMessageId, ...(name ? { name } : {}) }),
        },
      );
      const body = await jsonOrThrow<{ session: SessionMeta }>(
        res,
        'fork session',
      );
      return body.session;
    },

    async listFiles(scope, path, opts): Promise<FileListing> {
      const params = new URLSearchParams();
      if (path) params.set('path', path);
      if (opts?.cursor) params.set('cursor', opts.cursor);
      const qs = params.size ? `?${params.toString()}` : '';
      const url =
        scope === 'persistent'
          ? `/api/files/persistent${qs}`
          : `/api/files/workspace/${encodeURIComponent(opts?.sessionId ?? '')}${qs}`;
      const res = await authedFetch(url, { signal: opts?.signal });
      return await jsonOrThrow<FileListing>(res, 'list files');
    },

    async readFileText(scope, path, opts) {
      const params = new URLSearchParams({ path });
      const url =
        scope === 'persistent'
          ? `/api/files/persistent/read?${params.toString()}`
          : `/api/files/workspace/${encodeURIComponent(
              opts?.sessionId ?? '',
            )}/read?${params.toString()}`;
      const res = await authedFetch(url, { signal: opts?.signal });
      if (!res.ok) {
        let body: unknown = null;
        try {
          body = await res.json();
        } catch {
          // Body may be raw bytes.
        }
        throw new ApiError(
          res.status,
          `read file failed (${res.status})`,
          body,
        );
      }
      const mediaType =
        res.headers.get('content-type')?.split(';')[0]?.trim() ??
        'application/octet-stream';
      const text = await res.text();
      return { text, mediaType };
    },

    async issueMacPairingCode() {
      const res = await authedFetch('/api/mac/issue-code', { method: 'POST' });
      return await jsonOrThrow<{ code: string; expiresAt: number }>(
        res,
        'issue Mac pairing code',
      );
    },

    async listMacDevices(opts) {
      const res = await authedFetch('/api/mac/devices', {
        signal: opts?.signal,
      });
      const body = await jsonOrThrow<{ devices: MacDevice[] }>(
        res,
        'list Mac devices',
      );
      return body.devices;
    },

    async revokeMacDevice(deviceId) {
      const res = await authedFetch(
        `/api/mac/devices/${encodeURIComponent(deviceId)}`,
        { method: 'DELETE' },
      );
      if (res.status === 204 || res.ok) return;
      await jsonOrThrow(res, 'revoke Mac device');
    },
  };
}
